import type { Styles } from '@adornis/ass/style.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import type { XContainerExtension } from '@adornis/buildify/client/extensions/ContainerExtension.js';
import {
  ContainerFlexExtension,
  XContainerFlexExtension,
} from '@adornis/buildify/client/extensions/ContainerFlexExtension.js';
import { EditExtensionData } from '@adornis/buildify/client/extensions/EditExtensionData.js';
import { type DefaultValue } from '@adornis/buildify/client/globals/enums.js';
import { ThrowEventMoveToAnchor } from '@adornis/buildify/client/globals/events.js';
import { __renderBase } from '@adornis/buildify/client/globals/methods.js';
import type { BuildifyExtension } from '@adornis/buildify/client/globals/types.js';
import type { Base } from '@adornis/buildify/db/Base.js';
import { BaseContainer } from '@adornis/buildify/db/extensions/BaseContainer.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import '@adornis/digitale-helden-shared/client/theme/d-hr.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { DubniumBaseContainerFlex } from 'db/buildify/container/DubniumBaseContainerFlex';
import { html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import type { DubniumBaseContainerGrid } from '../../../../../../db/buildify/container/DubniumBaseContainerGrid.js';
import { customContainerEditor } from '../helpers.js';

const OPTIONAL_MARGIN_DEFAULT_VALUES = {
  margin: { global: '0 0 48px 0', mobile: '0 0 32px 0' },
};

export const BuildifyCampusContainerFlexExtension = (
  additionalDefaultValues: Record<string, DefaultValue> = {},
): BuildifyExtension<DubniumBaseContainerFlex> => ({
  class: DubniumBaseContainerFlex,
  toolbar: { icon: 'diagram-cells', text: { default: 'Flex' } },
  defaultValues: {
    lineLeftColor: { global: '#afca0b' },
    lineLeftWidth: { global: '3px' },
    flexDirection: { global: 'column' },
    rowGap: { global: '20px', mobile: '16px' },
    columnGap: { global: '24px', mobile: '20px' },
    borderRadius: { global: '5px' },
    backgroundColor: { global: 'transparent' },
    ...additionalDefaultValues,
  },
  render(instance, { isSubelement, dropzoneBefore, dropzoneAfter }) {
    return html`
      <campus-container-flex-extension
        .content=${instance}
        .defaultValues=${instance.parentID
          ? this.defaultValues
          : { ...this.defaultValues, ...OPTIONAL_MARGIN_DEFAULT_VALUES }}
        ?sub=${isSubelement}
        .hasDropzoneBefore=${dropzoneBefore}
        .hasDropzoneAfter=${dropzoneAfter}
      ></campus-container-flex-extension>
    `;
  },
  editor(options) {
    if (!options.content.parentID) {
      options.contentController.registerDefaults(OPTIONAL_MARGIN_DEFAULT_VALUES, options.controllerBaseKeyPath);
    }

    return html`
      <d-flex space="sm">
        <!-- default -->
        ${EditExtensionData({
          host: options.host,
          content: {
            value: options.content,
            path: options.controllerBaseKeyPath,
            controller: options.contentController,
            extension: ContainerFlexExtension,
          },
          globalSettings: options.globalSettings,
          extensions: options.extensions,
        })}
        <!-- custom -->
        ${customContainerEditor(options)}
      </d-flex>
    `;
  },
  group: 'Layout',
});

@customElement('campus-container-flex-extension')
export class CampusContainerFlexExtension extends XContainerFlexExtension {
  override connectedCallback(): void {
    super.connectedCallback();

    this.addEventListener('click', () => {
      const content = this.content.value as Maybe<DubniumBaseContainerFlex>;
      if (!content) return;
      const link = content.link;
      if (!link) return;
      if (link.link?.startsWith('#')) {
        // * anchor logic
        const anchor = link.link.substring(1, link.link.length);
        return ThrowEventMoveToAnchor(this, anchor);
      }
      if (!link.resolvedLink) return;
      if (this.isEditMode) return;
      goTo(link.resolvedLink, { target: link.target || '_self' });
    });
  }

  protected override _content(content: DubniumBaseContainerFlex): any {
    return html` ${super._content(content)} ${this.__leftLine()} `;
  }

  protected override renderChild({
    extensions,
    content,
  }: {
    extensions: BuildifyExtension[];
    content: Base;
  }): Renderable | undefined {
    const isContainer = content instanceof BaseContainer;
    const defaultValues = isContainer ? { margin: { global: '0' } } : {};
    return __renderBase(extensions, content, { defaultValues });
  }

  protected __leftLine() {
    if (!this.getValue('hasLineLeft')) return nothing;
    return html`
      <d-flex
        ${css({
          background: this.getValue('lineLeftColor'),
          position: 'absolute',
          top: '1px',
          left: '0',
          height: 'calc(100% - 2px)',
          width: this.getValue('lineLeftWidth'),
          borderRadius: this.sizes.borderRadius,
          zIndex: '20',
        })}
      ></d-flex>
    `;
  }

  override styles() {
    return [
      ...super.styles(),
      {
        ':host': {
          display: 'flex',
          flexDirection: this.getValue('flexDirection'),
          justifyContent: this.getValue('justifyContent'),
          alignItems: this.getValue('alignItems'),
          rowGap: this.getValue('rowGap'),
          columnGap: this.getValue('columnGap'),
          ...defaultContainerStyling(this as any),
        },
      },
    ] as Styles[];
  }
}

export const defaultContainerStyling = (
  host: XContainerExtension<DubniumBaseContainerFlex | DubniumBaseContainerGrid>,
) => {
  const content = host.content.value;
  const padding = host.getValue('padding');

  return {
    position: 'relative',
    borderRadius: host.getValue('borderRadius'),
    backgroundColor: host.getValue('backgroundColor'),
    padding: host.isViewMode ? padding : padding ?? '10px',

    ...(!content?.parentID ? { margin: host.getValue('margin') } : {}),

    '&:hover': {
      ...(content?.link?.link
        ? {
            cursor: 'pointer',
            boxShadow: `2px 2px 11px 0 ${host.colors.tone.secondaryText}`,
          }
        : {}),
    },
  } as Styles;
};
